// @need-translate
import {MouseEvent, useCallback, useEffect, useState} from 'react';
import {t} from '../../../../../../common/getTranslations';
import {isTrue} from '../../../../../../common/shared/utils/isTrue';
import {stopPropagationEvent} from '../../../../../../common/shared/utils/stopPropagationEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Icon} from '../../../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import {ArrowDirection} from '../../../../../../design-system/molecules/Tooltip/ArrowDirection.enum';
import {Tooltip} from '../../../../../../design-system/molecules/Tooltip/Tooltip';
import {LOG_CLICK_EVENT} from '../../../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../../../logging/putTiaraObjectProps';
import {LOCAL_STORAGE_KEY_AUTO_TAP_TOOLTIP_HIDDEN} from '../constant/talkViewerConstant';
import {events} from '../constant/talkViewerEvents';
import {setIsAutoTapTooltipExposed} from '../state/talkViewerSlice';
import {AutoTapSpeed} from '../type/AutoTapSpeed.enum';
import {ViewingMode} from '../type/ViewingMode.enum';
import {ZIndex} from '../type/ZIndex.enum';

export function TalkViewerAutoTapContainer() {
  const {
    viewerMenuHeight,
    isViewerMenuExposed,
    viewingMode,
    isViewerEndExposed,
    isSettingMenuExposed,
    isAutoTapTooltipExposed,
  } = useTypedSelector(state => ({
    viewerMenuHeight: state.talkViewer.settings.viewerMenuHeight,
    isViewerMenuExposed: state.viewerMenu.isExposed,
    viewingMode: state.talkViewer.settings.viewingMode,
    isViewerEndExposed: state.viewerEnd.isExposed,
    isSettingMenuExposed: state.talkViewer.settingMenu.exposed,
    isAutoTapTooltipExposed: state.talkViewer.isAutoTapTooltipExposed,
  }));
  const isShowAutoTapController =
    isViewerMenuExposed &&
    !isViewerEndExposed &&
    !isSettingMenuExposed &&
    viewingMode === ViewingMode.Tap;
  const {bottom} = viewerMenuHeight;
  const dispatch = useTypedDispatch();

  useEffect(() => {
    const tooltipHidden = isTrue(
      localStorage.getItem(LOCAL_STORAGE_KEY_AUTO_TAP_TOOLTIP_HIDDEN) || 'false'
    );
    if (!tooltipHidden) {
      dispatch(setIsAutoTapTooltipExposed(true));
    }
  }, [dispatch]);

  const onClickSlowSpeed = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      function handler() {
        dispatch(events.autoTapChangeSpeedTapPressed(AutoTapSpeed.Slow));
      }
      LOG_CLICK_EVENT('자동스크롤_속도_클릭', e, handler);
    },
    [dispatch]
  );
  const onClickNormalSpeed = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      function handler() {
        dispatch(events.autoTapChangeSpeedTapPressed(AutoTapSpeed.Normal));
      }
      LOG_CLICK_EVENT('자동스크롤_속도_클릭', e, handler);
    },
    [dispatch]
  );
  const onClickFastSpeed = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      function handler() {
        dispatch(events.autoTapChangeSpeedTapPressed(AutoTapSpeed.Fast));
      }
      LOG_CLICK_EVENT('자동스크롤_속도_클릭', e, handler);
    },
    [dispatch]
  );
  const [isAutoTapMenuActivated, setIsAutoTapMenuActivated] = useState(false);

  const closeTooltip = useCallback(() => {
    dispatch(setIsAutoTapTooltipExposed(false));
  }, [dispatch]);

  const onClickActiveAutoTap = useCallback(
    (e: MouseEvent) => {
      function handler() {
        setIsAutoTapMenuActivated(true);
        closeTooltip();
      }
      LOG_CLICK_EVENT('자동스크롤_클릭', e, handler);
    },
    [closeTooltip]
  );

  const onClickInactiveAutoTap = useCallback(() => {
    setIsAutoTapMenuActivated(false);
  }, []);

  useEffect(() => {
    if (!isShowAutoTapController) {
      setIsAutoTapMenuActivated(false);
    }
  }, [isShowAutoTapController]);

  return (
    <div
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={twMerge(
        'auto-tap-container',
        'fixed right-15pxr inline-flex w-40pxr flex-col',
        !isShowAutoTapController && 'hidden'
      )}
      style={{
        zIndex: ZIndex.AutotapController,
      }}
      onClick={stopPropagationEvent}
      {...putTiaraObjectProps({
        click: {layer1: '뷰어메뉴', layer2: '기타'},
      })}
    >
      <Tooltip
        arrowDirection={ArrowDirection.Right}
        showTooltip={isAutoTapTooltipExposed}
        text={'자동 탭 넘김으로\n편하게 감상하세요!'}
        className="absolute top-1/2 right-[calc(100%+16px)] -translate-y-1/2"
        onClickTooltipCancel={closeTooltip}
      />
      {!isAutoTapMenuActivated && (
        <button
          className="flex h-40pxr w-40pxr items-center justify-center rounded-full border-1 border-solid border-viewer-auto bg-theme-solid-100"
          onClick={onClickActiveAutoTap}
        >
          <Icon type={IconType.ViewerPlayS} className="h-24pxr w-24pxr" />
        </button>
      )}
      {isAutoTapMenuActivated && (
        <>
          <button
            className="mb-6pxr flex h-40pxr w-40pxr items-center justify-center rounded-full border-1 border-solid border-theme-transparent-20 bg-theme-solid-0"
            onClick={onClickFastSpeed}
            {...putTiaraObjectProps({
              click: {copy: '2x'},
            })}
          >
            <Text className="font-small2-bold text-theme-solid-100">
              {t('viewer_accessibility_common_menu_two')}
            </Text>
          </button>
          <button
            className="mb-6pxr flex h-40pxr w-40pxr items-center justify-center rounded-full border-1 border-solid border-theme-transparent-20 bg-theme-solid-0"
            onClick={onClickNormalSpeed}
            {...putTiaraObjectProps({
              click: {copy: '1x'},
            })}
          >
            <Text className="font-small2-bold text-theme-solid-100">
              {t('viewer_accessibility_common_menu_one')}
            </Text>
          </button>
          <button
            className="mb-6pxr flex h-40pxr w-40pxr items-center justify-center rounded-full border-1 border-solid border-theme-transparent-20 bg-theme-solid-0"
            onClick={onClickSlowSpeed}
            {...putTiaraObjectProps({
              click: {copy: '0.5x'},
            })}
          >
            <Text className="font-small2-bold text-theme-solid-100">
              {t('viewer_accessibility_common_menu_half')}
            </Text>
          </button>
          <button
            className="flex h-40pxr w-40pxr items-center justify-center rounded-full border-1 border-solid border-viewer-auto bg-theme-solid-100"
            onClick={onClickInactiveAutoTap}
          >
            <Icon type={IconType.ViewerCloseS} className="h-24pxr w-24pxr" />
          </button>
        </>
      )}
      <style jsx>
        {`
          .auto-tap-container {
            bottom: ${bottom + 15}px;
            bottom: calc(env(safe-area-inset-bottom) + ${bottom + 15}px);
          }
        `}
      </style>
    </div>
  );
}
